import {
  useExportOutput,
  useFileExportStatus,
  usePrintOutput,
} from "@bms/hooks";
import { GridApi } from "ag-grid-community";
import { notification } from "antd";
import dayjs from "dayjs";
import { CSSProperties, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { uid } from "uid";

export type ReceivedProps = Record<string, any>;

const SIMPLE_REPORT_NOTIFICATION_STYLE: CSSProperties = {
  width: "fit-content",
  maxWidth: "calc(100vw - 48px)",
  whiteSpace: "nowrap",
};

const useOutput = (props: ReceivedProps) => {
  const outputExportUuid = localStorage.getItem("output_export_uuid");

  const { control, setValue, getValues, register, handleSubmit, watch } =
    useForm();
  const { mutate: mutatePrint, isLoading: isPrintSubmitting } =
    usePrintOutput();
  const { mutate: mutateExport, isLoading: isExportSubmitting } =
    useExportOutput();
  const { mutate: mutateFileExportStatus } = useFileExportStatus();

  const [gridApi, setGridApi] = useState<GridApi | undefined>();
  const [filterParams, setFilterParams] = useState<Record<string, any>>({
    status: ["請求済のみ"],
    address_payment_type: ["all"],
  });
  const [statusFileExport, setStatusFileExport] = useState<boolean>(false);
  const isSimpleReportSelected = (watch("print_type") ?? []).includes(
    "simple_report"
  );

  const openSimpleReportWarning = (message: string) => {
    notification["warning"]({
      message,
      style: SIMPLE_REPORT_NOTIFICATION_STYLE,
    });
  };

  const onSubmit = (values: Record<string, any>) => {
    setFilterParams({
      ...filterParams,
      status: values?.status,
      address_payment_type: values?.address_payment_type,
      start_date: values?.start_date
        ? dayjs(values.start_date).format("YYYY/MM/DD")
        : undefined,
      end_date: values?.end_date
        ? dayjs(values.end_date).format("YYYY/MM/DD")
        : undefined,
      keyword: values?.keyword || undefined,
    });
  };

  const handleCheck = (checkedId: any, name: string) => {
    const { print_type, export_type } = getValues();
    const ids = name === "print_type" ? print_type : export_type;
    const newIds = ids?.includes(checkedId)
      ? ids?.filter((id: any) => id !== checkedId)
      : [...(ids ?? []), checkedId];
    return newIds;
  };

  const onExport = () => {
    const values = getValues();
    if (!values?.export_type || !gridApi) return;
    const uuid = uid();
    const rowSelected = gridApi.getSelectedRows();

    if (rowSelected.length === 0) {
      notification["warning"]({
        message: "サービスカードを選択してください。",
      });
    } else {
      mutateExport(
        {
          export_type: values?.export_type,
          service_card_ids: rowSelected.map((item) => ({ id: item.id })),
          uuid,
        },
        {
          onSuccess(res: any) {
            mutateFileExportStatus(
              { uuid },
              {
                onSuccess(data: any) {
                  if (data?.data?.status === "done") {
                    const link = document.createElement("a");
                    link.href = data?.data?.file_url;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setStatusFileExport(true);
                  } else {
                    localStorage.setItem("output_export_uuid", uuid);
                  }
                },
              }
            );

            notification["warning"]({
              message: res?.data?.message,
            });
          },
          onError(error: any) {
            notification["error"]({
              message: { error }?.error?.response?.data?.error || "Failed",
            });
          },
        }
      );
    }
  };

  const onPrint = () => {
    const values = getValues();
    const printTypes = values?.print_type ?? [];
    const hasSimpleReport = printTypes.includes("simple_report");

    if (!printTypes.length) return;

    if (hasSimpleReport) {
      if (printTypes.length > 1) {
        openSimpleReportWarning("簡易報告書は単独で印刷してください。");
        return;
      }

      if (!gridApi) return;
      const rowSelected = gridApi.getSelectedRows();

      if (rowSelected.length === 0 && !values?.is_checked_all) {
        notification["warning"]({
          message: "サービスカードを選択してください。",
        });
        return;
      }

      if (
        !values?.simple_report_start_date ||
        !values?.simple_report_end_date
      ) {
        openSimpleReportWarning("簡易報告書の完了日範囲を指定してください。");
        return;
      }

      const simpleStart = dayjs(values.simple_report_start_date);
      const simpleEnd = dayjs(values.simple_report_end_date);
      if (simpleStart.isAfter(simpleEnd, "day")) {
        notification["warning"]({
          message: "開始日は終了日より前である必要があります。",
          style: SIMPLE_REPORT_NOTIFICATION_STYLE,
        });
        return;
      }

      mutatePrint(
        {
          print_type: printTypes,
          service_card_ids: rowSelected.map((item) => ({ id: item.id })),
          simple_report_start_date: dayjs(
            values.simple_report_start_date
          ).format("YYYY/MM/DD"),
          simple_report_end_date: dayjs(values.simple_report_end_date).format(
            "YYYY/MM/DD"
          ),
          ...(values?.is_checked_all ? { is_checked_all: true } : {}),
        },
        {
          onSuccess(res: any) {
            const pdfUrl = res?.data?.pdf_url;
            if (pdfUrl) {
              const link = document.createElement("a");
              link.href = pdfUrl;
              link.download = "";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          },
          onError(error: any) {
            notification["error"]({
              message: { error }?.error?.response?.data?.error || "Failed",
            });
          },
        }
      );
      return;
    }

    if (!gridApi) return;
    const rowSelected = gridApi.getSelectedRows();

    if (rowSelected.length === 0) {
      notification["warning"]({
        message: "サービスカードを選択してください。",
      });
    } else {
      mutatePrint(
        {
          print_type: printTypes,
          id: rowSelected?.[0]?.id,
          service_card_ids: rowSelected.map((item) => ({ id: item.id })),
        },
        {
          onSuccess(res: any) {
            const pdfUrl = res?.data?.pdf_url;
            if (pdfUrl) {
              const link = document.createElement("a");
              link.href = pdfUrl;
              link.download = "";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          },
          onError(error: any) {
            notification["error"]({
              message: { error }?.error?.response?.data?.error || "Failed",
            });
          },
        }
      );
    }
  };

  useEffect(() => {
    if (!outputExportUuid || statusFileExport) return;
    const getFile = setInterval(() => {
      mutateFileExportStatus(
        { uuid: outputExportUuid },
        {
          onSuccess(data: any) {
            if (data?.data?.status === "done") {
              localStorage.removeItem("output_export_uuid");
              const link = document.createElement("a");
              link.href = data?.data?.file_url;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          },
        }
      );
    }, 10000);
    return () => {
      clearInterval(getFile);
    };
  }, [mutateFileExportStatus, outputExportUuid, statusFileExport]);

  return {
    ...props,
    control,
    filterParams,
    isPrintSubmitting,
    isExportSubmitting,
    gridApi,
    setGridApi,
    handleCheck,
    isSimpleReportSelected,
    onExport,
    onPrint,
    setFilterParams,
    onSubmit,
    setValue,
    getValues,
    register,
    handleSubmit,
  };
};

export type Props = ReturnType<typeof useOutput>;

export default useOutput;
